import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Smartphone, Bell, CalendarDays, Users, Briefcase, Sparkles, X, BadgePercent } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/AuthContext";
import { isProfessional } from "@/lib/constants";

export const PLAY_STORE_URL = "https://play.google.com/store/apps/details?id=com.taxmandi.app";
const KEY = "tm_app_promo_at";
const PERKS = [
  [Bell, "Instant notifications for messages, leads and job replies"],
  [Users, "Respond to client requirements faster"],
  [CalendarDays, "Book consultations, events and webinars"],
  [Briefcase, "Firm profiles and team discovery"],
  [Sparkles, "New features land in the app first"],
];

export const AppPromo = () => {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);

  const visitor = user === false;

  useEffect(() => {
    if (!visitor && !isProfessional(user)) return;
    const last = Number(localStorage.getItem(KEY) || 0);
    if (Date.now() - last > 24 * 3600 * 1000) {
      const t = setTimeout(() => setOpen(true), 1500);
      return () => clearTimeout(t);
    }
  }, [user, visitor]);

  const close = () => {
    localStorage.setItem(KEY, String(Date.now()));
    setOpen(false);
  };

  if (visitor) return <VisitorPromo open={open} close={close} />;
  return (
    <Dialog open={open} onOpenChange={(o) => !o && close()}>
      <DialogContent className="max-w-md" data-testid="app-promo-dialog">
        <DialogHeader>
          <div className="mb-2 grid h-12 w-12 place-items-center rounded-2xl bg-tm-green text-white"><Smartphone className="h-6 w-6" /></div>
          <DialogTitle className="font-display text-xl text-tm-green">Get the Taxmandi app</DialogTitle>
          <DialogDescription>For the best experience as a professional, use the Taxmandi app. Same account, same data, more features.</DialogDescription>
        </DialogHeader>
        <ul className="space-y-2.5 text-sm text-slate-700">
          {PERKS.map(([Icon, t]) => <li key={t} className="flex gap-2.5"><Icon className="h-4 w-4 shrink-0 text-tm-gold" />{t}</li>)}
        </ul>
        <div className="mt-2 flex flex-col gap-2 sm:flex-row">
          <Button asChild className="flex-1 rounded-full bg-tm-green hover:bg-tm-green/90" data-testid="app-promo-download-button">
            <a href={PLAY_STORE_URL} target="_blank" rel="noreferrer" onClick={close}>Download on Google Play</a>
          </Button>
          <Button variant="outline" className="flex-1 rounded-full" onClick={close} data-testid="app-promo-dismiss-button">Continue on website</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

const VisitorPromo = ({ open, close }) => (
  <Dialog open={open} onOpenChange={(o) => !o && close()}>
    <DialogContent className="max-w-md overflow-hidden p-0 [&>button]:text-white [&>button]:opacity-90" data-testid="visitor-promo-dialog">
      <div className="bg-tm-green p-6 text-white">
        <span className="inline-flex items-center gap-1.5 rounded-full bg-tm-saffron px-3 py-1 text-xs font-bold text-tm-dark"><BadgePercent className="h-4 w-4" />50% OFF ON LISTING</span>
        <DialogTitle className="mt-4 font-display text-2xl font-extrabold text-white">Are you a professional?</DialogTitle>
        <DialogDescription className="mt-2 text-white/80">CA, CS, CMA, tax or accounting professional? Join Taxmandi and get 50% off on your listing. Download the app now.</DialogDescription>
      </div>
      <div className="flex flex-col gap-2 p-6">
        <Button asChild className="rounded-full bg-tm-green hover:bg-tm-green/90" data-testid="visitor-promo-download-button">
          <a href={PLAY_STORE_URL} target="_blank" rel="noreferrer" onClick={close}>Download app now</a>
        </Button>
        <Button asChild variant="outline" className="rounded-full" data-testid="visitor-promo-join-button"><Link to="/register" onClick={close}>Join us on the website</Link></Button>
        <button onClick={close} className="mt-1 text-sm text-slate-500 hover:underline" data-testid="visitor-promo-dismiss-button">I'm a client, continue browsing</button>
      </div>
    </DialogContent>
  </Dialog>
);

export const OpenInAppBar = () => {
  const { user } = useAuth();
  const [hidden, setHidden] = useState(() => sessionStorage.getItem("tm_app_bar") === "0");
  if (hidden || !isProfessional(user)) return null;
  const close = () => { sessionStorage.setItem("tm_app_bar", "0"); setHidden(true); };
  return (
    <div className="flex items-center gap-3 bg-tm-green px-4 py-2 text-white lg:hidden" data-testid="open-in-app-bar">
      <Smartphone className="h-4 w-4 shrink-0 text-tm-gold" />
      <p className="flex-1 text-xs">Better experience in the Taxmandi app</p>
      <a href={PLAY_STORE_URL} target="_blank" rel="noreferrer" className="rounded-full bg-white px-3 py-1 text-xs font-semibold text-tm-green" data-testid="open-in-app-button">Open in app</a>
      <button onClick={close} aria-label="Close" data-testid="open-in-app-close"><X className="h-4 w-4" /></button>
    </div>
  );
};

export const AudienceStrip = () => (
  <div className="mx-auto grid max-w-7xl gap-3 px-6 md:grid-cols-2" data-testid="audience-strip">
    <div className="rounded-2xl border bg-white p-5">
      <p className="text-xs font-semibold uppercase tracking-[0.2em] text-tm-green-light">For clients & businesses</p>
      <p className="mt-1 text-sm text-slate-700">Find a verified CA, CS or tax expert right here on the website and contact them directly by chat or WhatsApp. Free.</p>
    </div>
    <div className="flex items-center gap-4 rounded-2xl bg-tm-green p-5 text-white">
      <div className="flex-1">
        <p className="text-xs font-semibold uppercase tracking-[0.2em] text-tm-gold">For professionals</p>
        <p className="mt-1 text-sm text-white/85">For the best experience, leads and new features, use the Taxmandi app.</p>
      </div>
      <a href={PLAY_STORE_URL} target="_blank" rel="noreferrer" className="shrink-0 rounded-full bg-white px-4 py-2 text-sm font-semibold text-tm-green" data-testid="audience-strip-download">Get the app</a>
    </div>
  </div>
);
